// src/components/PlanetConjunctions.jsx

import React, { useState, useMemo } from "react";
import "../styles/ui-foundation.css";

/* =========================================================
   Helper: Orb → Interaction Label (Neutral Framing)
========================================================= */
function getInteractionLabel(orbType) {
  if (!orbType) return "Layered Interaction";
  const o = orbType.toLowerCase();
  if (o.includes("close")) return "Tightly Interwoven";
  if (o.includes("wide")) return "Subtle Interaction";
  return "Layered Interaction";
}

/* =========================================================
   Component
   mode:
     - "REAL"     → Insight (Full Depth)
     - "SUMMARY"  → Growth + Premium Unlock
========================================================= */
function PlanetConjunctions({ conjunctions, mode = "REAL" }) {
  const [openIndex, setOpenIndex] = useState(null);


  const clusters = useMemo(
    () => (Array.isArray(conjunctions?.by_house) ? conjunctions.by_house : []),
    [conjunctions?.by_house]
  );

  /* =========================
     SUMMARY (Visible in ALL plans)
    ========================== */
    const summary = useMemo(() => {
      const total = clusters.length || 0;

      const houses = clusters
        .map((c) => c.house)
        .filter(Boolean)
        .map((h) => `H-${h}`);

      const largestCluster =
        clusters.length > 0
          ? Math.max(...clusters.map((c) => c.conjunction_size || 0))
          : 0;

      const tightCount = clusters.filter(
        (c) =>
          c.orb_type &&
          c.orb_type.toLowerCase().includes("close")
      ).length;

      return {
        total,
        houses: [...new Set(houses)],
        largestCluster,
        tightCount,
      };
    }, [clusters]);

  /* =========================
     SAFETY GUARD
  ========================== */
  if (
    !conjunctions ||
    !Array.isArray(conjunctions.by_house) ||
    conjunctions.by_house.length === 0
  ) {
    return (
      <div className="text-muted">
        No notable conjunctions detected in this chart (based on current rules).
        Single-planet houses and minor aspects are not shown here.
      </div>
    );
  }

  

  /* =========================
     LOCK BLOCK (Growth / Premium)
  ========================== */
  const LockBlock = () => (

    <div
      style={{
        marginTop: 36,
        padding: 20,
        borderRadius: 14,
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(148,163,184,0.15)",
      }}
    >
      
      {/* Section Label */}
      <div
        style={{
          fontSize: 11,
          letterSpacing: 0.8,
          textTransform: "uppercase",
          color: "#64748b",
          marginBottom: 10,
        }}
      >
        Interpretive Depth
      </div>

      {/* Calm Heading */}
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        🔒 Detailed Interaction Analysis
      </div>

      {/* Body */}
      <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.7 }}>
        This section expands beyond structural patterns into
        psychological expression, developmental tensions,
        and integration guidance.
        <br /><br />
        Insight plan provides full interpretive depth across all modules.
      </div>
    </div>
);

  /* =========================
     RENDER
  ========================== */
  return (
    <div className="conjunction-root">

      {/* INTRO */}
      <div className="card">
        <div className="conjunction-intro">
          When multiple planets share the same house, their themes interact and blend.
          This section explores how those interactions shape recurring psychological
          and situational patterns.
        </div>
      </div>

      {/* SUMMARY CARD */}
      <div className="card">
        <div style={{ fontWeight: 700, marginBottom: 8 }}>
          Conjunction Summary
        </div>

        <div className="pc-summary-grid">
          <div>Total clusters detected: {summary.total}</div>
          <div>
            Houses involved:{" "}
            {summary.houses.length > 0
              ? summary.houses.join(", ")
              : "—"}
          </div>
          <div>Largest cluster: {summary.largestCluster} planets</div>
          <div>
            Tight interactions (close orb): {summary.tightCount}
          </div>
        </div>

        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: "#9ca3af",
          }}
        >
          Conjunctions reflect blended planetary themes.
          They describe interaction patterns — not fixed outcomes.
        </div>
      </div>

      {/* =========================================================
         DETAIL LAYER
      ========================================================= */}

      {/* ================= INSIGHT (FULL DEPTH) ================= */}
      {mode === "REAL" &&
        clusters.map((c, idx) => {
          const isOpen = openIndex === idx;

          return (
            <div
              key={idx}
              className="pc-accordion-card"
              style={{
                marginBottom: 20,
                padding: 16,
                borderRadius: 12,
                background: "rgba(30,41,59,0.82)",
                border: isOpen
                  ? "1px solid rgba(251,191,36,0.55)"
                  : "1px solid rgba(148,163,184,0.25)",
                cursor: "pointer",
                transition: "all 0.18s ease",
              }}
              onClick={() =>
                setOpenIndex(isOpen ? null : idx)
              }
            >
              {/* HEADER */}
              <div style={{ fontWeight: 700 }}>
                House {c.house ?? "?"}
                {c.role_label && ` — ${c.role_label}`}
                <span style={{ float: "right", opacity: 0.6 }}>
                  {isOpen ? "▲" : "▼"}
                </span>
              </div>

              {/* META GRID */}
              <div
                style={{
                  marginTop: 14,
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: 12,
                }}
              >
                <MetaBox label="Planets" value={Array.isArray(c.planets) ? c.planets.join(", ") : "N/A"} />
                <MetaBox label="House" value={`H-${c.house ?? "?"}`} />
                <MetaBox label="Cluster Size" value={c.conjunction_size || 0} />
                <MetaBox label="Interaction" value={getInteractionLabel(c.orb_type)} />
              </div>

              <div
                style={{
                  marginTop: 8,
                  fontSize: 12,
                  fontStyle: "italic",
                  color: "#9ca3af",
                }}
              >
                Orb reflects closeness of interaction — not strength, benefit, or outcome.
              </div>

              {/* CORE INTERACTION */}
              {c.summary && (
                <>
                  <div className="yoga-subsection-title">
                    Core Interaction
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    {c.summary}
                  </div>
                </>
              )}

              {/* DEEP LAYERS — RESTORED */}
              {isOpen && (
                <>
                  {c.key_effects?.length > 0 && (
                    <>
                      <div className="yoga-subsection-title">
                        Psychological Pattern
                      </div>
                      <ul>
                        {c.key_effects.map((text, i) => (
                          <li key={i}>{text}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {c.balancing_actions?.length > 0 && (
                    <>
                      <div className="yoga-subsection-title">
                        Awareness Integration
                      </div>
                      <ul>
                        {c.balancing_actions.map((text, i) => (
                          <li key={i}>{text}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {c.challenges?.length > 0 && (
                    <>
                      <div className="yoga-subsection-title">
                        Developmental Tension
                      </div>
                      <ul>
                        {c.challenges.map((text, i) => (
                          <li key={i}>{text}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {c.yogas?.length > 0 && (
                    <div
                      style={{
                        marginTop: 18,
                        padding: 14,
                        borderRadius: 10,
                        background: "rgba(15,23,42,0.6)",
                        border: "1px solid rgba(148,163,184,0.15)",
                      }}
                    >
                      <div className="yoga-subsection-title">
                        Yoga Emergence
                      </div>

                      {c.yogas.map((y, yi) => (
                        <div key={yi} style={{ marginTop: 8 }}>
                          <div style={{ fontWeight: 500 }}>
                            {y.name}
                          </div>

                          {y.summary && (
                            <div style={{ fontSize: 13, color: "#cbd5e1" }}>
                              {y.summary}
                            </div>
                          )}
                        </div>
                      ))}

                      <div
                        style={{
                          fontSize: 12,
                          fontStyle: "italic",
                          color: "#9ca3af",
                          marginTop: 10,
                        }}
                      >
                        These classical yoga-forms arise from planetary blending.
                        Confirmed and strength-evaluated yogas are listed in the Yoga Analysis tab.
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}

      {/* SUMMARY MODE */}
      {mode === "SUMMARY" && <LockBlock />}
    </div>
  );
}

/* Reusable MetaBox */
function MetaBox({ label, value }) {
  return (
    <div
      style={{
        padding: "10px 12px",
        borderRadius: 10,
        background: "rgba(15,23,42,0.6)",
        border: "1px solid rgba(148,163,184,0.15)",
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: "#94a3b8",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "#e2e8f0",
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default PlanetConjunctions;